import type { RowPredicateLite } from '../../../core/bridge/connect-client';
import type { TableColumn } from './integration-sources';

/*
 * In-cell editing for the table screen — the assignment's "editing that reads as
 * editing rather than as form submission".
 *
 * This is the pure half: which cells may be edited at all, how a row is addressed,
 * and how typed text becomes a value. It lives apart from the widget because these
 * are the rules that are silently catastrophic when wrong — an edit is the one
 * thing the table screen does that CHANGES a customer's production database — and
 * they should be provable without a DOM.
 *
 * The addressing rule, stated once: a row is named by its KEY COLUMNS plus the
 * ORIGINAL VALUE of the cell being edited. The key columns say which row; the
 * original value says "…and only if nobody has changed it since I loaded it". A
 * concurrent edit therefore matches nothing, and the source refuses rather than
 * overwriting a change the person never saw. Server-side the whole statement runs
 * in a transaction that commits only if exactly one row changed, so this predicate
 * is the intent, not the enforcement.
 */

export type CellValue = string | number | boolean | null;

/** Which columns of a row may be edited.
 *
 *  Empty when the target has no row identity (`rowKey` empty) — with no way to
 *  name a row there is no safe edit, and offering the control would only earn a
 *  refusal. Key columns are themselves excluded: changing the value you are
 *  addressing the row BY is a re-key, a different and much larger operation than
 *  correcting a cell. Read-only columns (a view's, per the schema's own flag) are
 *  excluded too. */
export function editableColumns(columns: readonly TableColumn[], rowKey: readonly string[]): Set<string> {
  if (rowKey.length === 0) return new Set();
  const keys = new Set(rowKey.map((k) => k.toLowerCase()));
  const out = new Set<string>();
  for (const c of columns) {
    if (keys.has(c.key.toLowerCase())) continue;
    if (c.writable === false) continue;
    out.add(c.key);
  }
  return out;
}

/** Build the predicate that names ONE row for an edit of `column`.
 *
 *  Returns an empty array when the row cannot be safely addressed, and the caller
 *  MUST then refuse the edit rather than send a wider predicate:
 *
 *   - a key column missing from the row, or holding null/undefined. `col = NULL`
 *     is never true in SQL, so such a predicate matches nothing; worse, dropping
 *     the term instead would widen the statement to every row sharing the rest.
 *   - no key columns at all.
 *
 *  The edited column's original value is added as a guard, EXCEPT when it is null:
 *  the same `= NULL` problem, and a row whose cell is empty is still perfectly
 *  addressable by its key alone. */
export function buildRowWhere(
  row: Readonly<Record<string, CellValue | undefined>>,
  rowKey: readonly string[],
  column: string,
): RowPredicateLite[] {
  if (rowKey.length === 0) return [];
  const where: RowPredicateLite[] = [];
  for (const k of rowKey) {
    const v = row[k];
    if (v === null || v === undefined) return []; // cannot address this row
    where.push({ column: k, op: 'eq', value: v });
  }
  const previous = row[column];
  if (previous !== null && previous !== undefined && !rowKey.includes(column)) {
    where.push({ column, op: 'eq', value: previous });
  }
  return where;
}

/** Turn what someone typed into the value to send, using the column's declared
 *  SQL type. An empty box means NULL rather than an empty string — "absent is not
 *  zero", and it is the only way to clear a cell.
 *
 *  A number box that does not parse, or a bool box that is not recognisable, falls
 *  back to the raw STRING and lets the source decide: refusing here would mean
 *  guessing at thirty engines' coercion rules, and a source's own error names the
 *  column and the type far better than we could. */
export function coerceCell(raw: string, sqlType?: string): CellValue {
  const t = (sqlType ?? '').toLowerCase();
  if (raw === '') return null;
  if (/bool/.test(t)) {
    const v = raw.trim().toLowerCase();
    if (v === 'true' || v === 't' || v === '1' || v === 'yes') return true;
    if (v === 'false' || v === 'f' || v === '0' || v === 'no') return false;
    return raw;
  }
  if (/int|serial/.test(t) && !/interval|point/.test(t)) {
    const n = Number(raw.trim());
    return Number.isInteger(n) && raw.trim() !== '' ? n : raw;
  }
  if (/float|double|real|numeric|decimal|money/.test(t)) {
    const n = Number(raw.trim());
    return Number.isFinite(n) && raw.trim() !== '' ? n : raw;
  }
  return raw;
}

/** How a cell's current value shows in the editor. null/undefined open EMPTY, so
 *  committing without typing leaves the cell null rather than writing the literal
 *  text "null" into a customer's database. */
export function editText(v: CellValue | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}

/** Whether a committed edit is actually a change. An edit that matches what was
 *  already there is not sent — a no-op UPDATE would still take a row lock and,
 *  because it must match exactly one row, could fail for no reason a person could
 *  understand. */
export function isChanged(previous: CellValue | undefined, next: CellValue): boolean {
  const before = previous === undefined ? null : previous;
  return before !== next;
}
